import { inject, provideAppInitializer } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiAuthService } from '../services/api-auth.service';
import { AuthService } from '../services/auth.service';
import { UsersApiService } from '../services/users-api.service';

/**
 * On SPA bootstrap, silently attempt to restore the session via the BFF's
 * HttpOnly refresh cookie. No JS-readable token storage remains, so the only
 * way to recover auth state after a reload is a /bff/auth/token/refresh
 * round-trip. On success, fetch the current user profile from /users/me to
 * synchronize AuthService's currentUser signal (F1).
 *
 * Skipped on `/auth/callback` — that route runs its own code-for-token
 * exchange and a parallel refresh here would race with it.
 */
export const authInitializerProvider = provideAppInitializer(() => {
  const apiAuthService = inject(ApiAuthService);
  const authService = inject(AuthService);
  const usersApi = inject(UsersApiService);

  if (typeof window === 'undefined' || window.location.pathname === '/auth/callback') {
    return;
  }

  return apiAuthService.tryRestoreSession().then(async (success) => {
    if (!success) {
      authService.clearAuthState();
      return;
    }
    try {
      const user = await firstValueFrom(usersApi.getMe());
      authService.setCurrentUser(user);
    } catch {
      // Token restored but profile fetch failed — keep the token, profile
      // will be lazily fetched on next authenticated request.
    }
  });
});
